import React, { useState } from "react";
import { Link } from "react-router-dom";
import MarketingShell from "../MarketingShell";
import SEOHead from "../../components/SEOHead";
import "./about-opal.css";

/**
 * About Opal — /about-opal
 *
 * Native implementation of the "About Opal" design prototype
 * (design-ref/public/About Opal.html), preserving the full behavior of the
 * legacy pages/AboutOpalPage.js:
 *   - SEOHead (same title/description/keywords/canonical/ogImage/schema)
 *   - click-to-enlarge lightbox on the opal-type diagrams
 *     (openLightbox/closeLightbox state, overlay click closes, close button,
 *     image click stops propagation)
 *   - react-router Links to /online-store and /free-museum in the CTA
 * The page is otherwise fully static.
 */

const FORMATION_STAGES = [
  {
    kicker: "130–100 MYA",
    kickerClass: "",
    title: "The ancient sea",
    text: "The Cretaceous Eromanga Sea laid down silica-bearing sediments — and even opalised fossils.",
  },
  {
    kicker: "100–65 MYA",
    kickerClass: " op-stage__kicker--teal",
    title: "The sea retreats",
    text: "Uplift and increasing aridity drove silica-rich water seeping down into the rock.",
  },
  {
    kicker: "30–5 MYA",
    kickerClass: " op-stage__kicker--turquoise",
    title: "Opal formation",
    text: "Silica spheres 150–400nm stacked into ordered layers — diffracting light into play-of-colour.",
  },
  {
    kicker: "TODAY",
    kickerClass: " op-stage__kicker--blue",
    title: "The present day",
    text: "Discovered in 1915 by Willie Hutchison; today the fields yield over 70% of the world's precious opal.",
  },
];

const OPAL_TYPES = [
  {
    name: "Solid Opals",
    description: "100% natural opal, shaped and polished to highlight their brilliance.",
    features: ["100% natural opal", "Most valuable & durable", "Can be worn daily", "Suitable for all jewellery"],
    featsClass: "",
    image: "/design/assets/opal-solid.png",
  },
  {
    name: "Doublet Opals",
    description: "A thin slice of light opal bonded to a darker backing to enhance colour.",
    features: [
      "Thin opal on dark backing",
      "Enhanced colour intensity",
      "More affordable",
      "Good for pendants & earrings",
    ],
    featsClass: " op-type__feats--teal",
    image: "/design/assets/opal-doublet.png",
  },
  {
    name: "Triplet Opals",
    description: "Three-layer construction with a clear protective quartz or crystal cap.",
    features: ["Three-layer construction", "Clear protective cap", "Magnified colours", "Most affordable option"],
    featsClass: " op-type__feats--turquoise",
    image: "/design/assets/opal-triplet.png",
  },
];

const CARE_DOS = [
  "Clean with mild soap & water",
  "Store in a soft cloth or box",
  "Remove before swimming",
  "Keep from harsh chemicals",
];

const CARE_DONTS = [
  "Expose to extreme temp changes",
  "Use ultrasonic cleaners",
  "Soak doublets / triplets",
  "Wear during heavy activity",
];

const RAboutOpalPage = () => {
  const [lightboxImage, setLightboxImage] = useState(null);

  const openLightbox = (image, title) => {
    setLightboxImage({ src: image, title });
  };

  const closeLightbox = () => {
    setLightboxImage(null);
  };

  const schemaData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "About Australian Opals",
    "description":
      "Learn about Australian opals including solid opals, doublets, and triplets. Understand the different types, their characteristics, and how to care for your opal jewelry.",
    "url": "https://umoonaopalmine.com.au/about-opal",
  };

  return (
    <MarketingShell pageClass="pg-about-opal">
      <SEOHead
        title="About Australian Opals - Types, Care & Information | Umoona Opal Mine"
        description="Learn about Australian opals including solid opals, doublets, and triplets. Understand the different types, their characteristics, and how to care for your opal jewelry."
        keywords="Australian opals, opal types, solid opal, doublet opal, triplet opal, opal care, Coober Pedy opals, opal information"
        canonical="/about-opal"
        ogImage="/og-umoona.jpg"
        schemaData={schemaData}
      />

      {/* HERO */}
      <div className="u-hero">
        <img className="u-hero__img" src="/design/assets/opal-hero.jpg" alt="Vivid Australian opal displaying colourful play-of-colour, formed beneath the Coober Pedy desert" />
        <div className="u-hero__scrim" />
        <div className="u-hero__inner">
          <div className="u-hero__eyebrow">Australia's national gemstone</div>
          <h1 className="u-hero__title">About Australian Opals</h1>
          <p className="u-hero__sub">
            Discover the beauty, types and characteristics of one of the world's most unique gemstones — formed over
            millions of years beneath the Coober Pedy desert.
          </p>
        </div>
      </div>

      {/* WHAT IS OPAL — photo + panel split */}
      <section className="u-split">
        <div className="u-photoband">
          <img src="/design/assets/opal-what.jpg" alt="Rough Australian opal revealing its natural rainbow play-of-colour" loading="lazy" decoding="async" />
        </div>
        <div className="op-panel">
          <div className="u-eyebrow">🌏 Geological marvel</div>
          <h2 className="u-h2">What is opal?</h2>
          <p className="op-panel__text">
            Opals are Australia's national gemstone, renowned worldwide for their unique play of colour. Formed over
            millions of years, these precious stones contain tiny silica spheres that diffract light to create their
            characteristic rainbow.
          </p>
          <p className="op-panel__text">
            They can be cut, polished and set in many ways, and are generally classified as solid opals or composite
            opals.
          </p>
        </div>
      </section>

      {/* HOW OPAL FORMED — 4 formation stages */}
      <section className="u-sectpad u-sect-deep">
        <div className="u-eyebrow">How opal formed in Coober Pedy</div>
        <h2 className="u-h2">
          The story begins over 100 million years ago, when an ancient inland sea covered the outback.
        </h2>
        <div className="u-grid2">
          {FORMATION_STAGES.map((stage) => (
            <div className="op-stage" key={stage.title}>
              <div className={"op-stage__kicker" + stage.kickerClass}>{stage.kicker}</div>
              <div className="op-stage__title">{stage.title}</div>
              <p className="op-stage__text">{stage.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* TYPES OF OPAL — solid / doublet / triplet */}
      <section className="u-sectpad u-sect-test">
        <div className="u-eyebrow">Types of opal</div>
        <h2 className="u-h2">Solid, doublet and triplet</h2>
        <div className="u-grid3">
          {OPAL_TYPES.map((type) => (
            <div className="op-type" key={type.name}>
              <button
                type="button"
                className="op-type__diagram"
                onClick={() => openLightbox(type.image, `${type.name} Structure`)}
                aria-label={`Enlarge ${type.name} diagram`}
              >
                <img src={type.image} alt={`${type.name} Structure`} loading="lazy" decoding="async" />
                <span className="op-type__zoom" aria-hidden="true">
                  Click to enlarge
                </span>
              </button>
              <div className="op-type__body">
                <div className="op-type__title">{type.name}</div>
                <p className="op-type__desc">{type.description}</p>
                <div className={"op-type__feats" + type.featsClass}>
                  {type.features.map((feature, idx) => (
                    <React.Fragment key={feature}>
                      · {feature}
                      {idx < type.features.length - 1 ? <br /> : null}
                    </React.Fragment>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CARE GUIDE — panel + photo split */}
      <section className="u-split">
        <div className="op-panel">
          <div className="u-eyebrow">Care guide</div>
          <h2 className="u-h2">Caring for your opal jewellery</h2>
          <div className="op-caregrid">
            <div>
              <div className="op-carehead op-carehead--do">✓ DO</div>
              <div className="op-carelist">
                {CARE_DOS.map((item, idx) => (
                  <React.Fragment key={item}>
                    {item}
                    {idx < CARE_DOS.length - 1 ? <br /> : null}
                  </React.Fragment>
                ))}
              </div>
            </div>
            <div>
              <div className="op-carehead op-carehead--dont">✕ DON'T</div>
              <div className="op-carelist">
                {CARE_DONTS.map((item, idx) => (
                  <React.Fragment key={item}>
                    {item}
                    {idx < CARE_DONTS.length - 1 ? <br /> : null}
                  </React.Fragment>
                ))}
              </div>
            </div>
          </div>
        </div>
        <div className="u-photoband">
          <img src="/design/assets/opal-care.jpg" alt="Opal jewellery being gently cleaned and cared for" loading="lazy" decoding="async" />
        </div>
      </section>

      {/* CTA */}
      <section className="u-sectpad u-sect-cta">
        <div className="u-ctacenter">
          <h2 className="u-ctacenter__title">Ready to own authentic Australian opals?</h2>
          <p className="u-ctacenter__sub">Every piece comes with a certificate of authenticity.</p>
          <div className="u-ctacenter__btns">
            <Link to="/online-store" className="u-btn u-btn--primary">
              Shop Authentic Opals
            </Link>
            <Link to="/free-museum" className="u-btn u-btn--ghost">
              Visit Free Museum
            </Link>
          </div>
        </div>
      </section>

      {/* LIGHTBOX (carried over from the legacy page: click diagram to enlarge) */}
      {lightboxImage && (
        <div
          className="op-lightbox"
          role="dialog"
          aria-modal="true"
          aria-label={lightboxImage.title}
          onClick={closeLightbox}
        >
          <div className="op-lightbox__frame">
            <button type="button" className="op-lightbox__close" onClick={closeLightbox} aria-label="Close">
              ✕
            </button>
            <img
              className="op-lightbox__img"
              src={lightboxImage.src}
              alt={lightboxImage.title}
              onClick={(e) => e.stopPropagation()}
              loading="lazy"
              decoding="async"
            />
          </div>
        </div>
      )}
    </MarketingShell>
  );
};

export default RAboutOpalPage;
